import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Bot, Users, FolderKanban, ShieldCheck, MessageSquare, FileSignature,
  DollarSign, Clock, Calendar, Bell, Sparkles, Workflow, UploadCloud, Video,
  Mic, BarChart3, Gift, Globe, ScrollText, Search, Sun, BookOpen,
  ArrowRight, ArrowDown, Mail, Shield, Lock, Server,
  Briefcase, Stethoscope, GraduationCap, Scale, Megaphone, Code2, Hammer,
  ShoppingBag, PenTool, Building,
  UserPlus, ClipboardList, Handshake, FileCheck, Rocket, CheckCircle2,
} from 'lucide-react';
import { useBranding } from '@/contexts/BrandingContext';

// ---- Feature cards (flip on click) ----
const FEATURES = [
  { icon: FolderKanban, title: 'Unified project hub', funny: "No more 'where's that file?' at 2am. It's here. It's always here.", desc: 'Multi-brand portals with overview, timelines, progress.' },
  { icon: Bot, title: 'AI baked in', funny: 'Claude writes your status reports. You take the credit.', desc: 'Claude-powered summaries, lead scoring, weekly reports, follow-ups.' },
  { icon: ShieldCheck, title: 'Encrypted vault', funny: "Your client's Stripe key is safer here than in your Notes app. Lower bar, still true.", desc: 'AES-encrypted per-brand credentials, tokenized wizard submissions.' },
  { icon: MessageSquare, title: 'Live chat', funny: "Because 'did you get my email?' is a war crime.", desc: 'WebSocket real-time chat per project. No Slack invites needed.' },
  { icon: FileSignature, title: 'SOWs with e-sign', funny: 'DocuSign called. They want their $30/month back.', desc: 'Draft, send, countersign, PDF-export in one flow.' },
  { icon: DollarSign, title: 'Estimates + invoices', funny: 'Money in. Stripe, PayPal, Razorpay. Your accountant just sighed happily.', desc: 'Task catalog, PDFs, live payment status, 3 gateways.' },
  { icon: Clock, title: 'Time tracking', funny: 'Turn "I worked like 40 hours" into "40.25, invoiced at $127.50".', desc: 'Start/stop timers, billable/non-billable, linked to invoices.' },
  { icon: Calendar, title: 'Calendar & Zoom', funny: "Meetings that end ON time. Sorcery. Seriously.", desc: 'Google Calendar + Zoom S2S OAuth, 1-click scheduling.' },
  { icon: UploadCloud, title: 'R2 file uploads', funny: "Stop emailing 800MB screen recordings. Please. For everyone.", desc: 'Cloudflare R2 streaming, video thumbnails, inline previews.' },
  { icon: Video, title: 'Video annotations', funny: '"See 0:42 — that\'s the bug." Timestamped. Permanent. Actionable.', desc: 'Click-to-timestamp notes, playback trim, shareable.' },
  { icon: Workflow, title: 'Onboarding wizards', funny: "Turns 'can you send us a brief?' into a zero-meeting intake.", desc: 'Custom wizards, vault tokens, file/video upload, signature.' },
  { icon: Users, title: 'Leads → clients', funny: "Approve with one click. Block with less grace.", desc: 'Self-signup + wizard funnels, AI scoring, approve-to-portal flow.' },
  { icon: Bell, title: 'Webhooks + triggers', funny: "Your Slack bot will know before you do. That's kind of the point.", desc: '9-event catalog → Zapier / n8n / Make / Discord / Slack.' },
  { icon: Sparkles, title: 'Meeting notetaker', funny: "Paste transcript → AI summarizes → you look smart in the recap.", desc: 'Claude extracts action items, decisions, risks.' },
  { icon: Mic, title: 'Chief-of-staff', funny: "An AI that says 'tackle these 3 things today' — and it's right.", desc: 'Today view, AI priorities, overdue bugs, stale invoices.' },
  { icon: BarChart3, title: 'NPS + reports', funny: 'Quantify the love. Or the lack.', desc: 'One-click NPS surveys + weekly AI status reports per project.' },
  { icon: Gift, title: 'Referrals ledger', funny: "Pay your advocates. Watch your pipeline go ka-ching.", desc: 'Track introductions, auto-calc commission, mark paid.' },
  { icon: Globe, title: 'Brand mini-sites', funny: "One app. N brands. N public landings. M happy prospects.", desc: 'Per-brand public sites with case studies + CTAs.' },
  { icon: ScrollText, title: 'Audit log', funny: "Who did what, when. For when 'trust' isn't quite enough.", desc: 'Every approval, reveal, payment — logged with user + timestamp.' },
  { icon: Search, title: 'Global search', funny: "⌘K and forget where you buried that SOW.", desc: 'Semantic search across projects, clients, bugs, KB, SOWs.' },
];

// ---- Pain-point domains ----
const DOMAINS = [
  {
    id: 'freelance', label: 'Freelancers', icon: PenTool,
    pain: "You're juggling 6 clients in 12 tools — Notion for notes, Loom for demos, Stripe for invoices, email for files, Calendly for calls, WhatsApp for panic. Nothing talks to anything. Half your day is admin. The other half is chasing payment.",
    solution: "Every client gets one portal URL. Chat, SOW, invoice, file, video feedback — all in one place. Send a link, get paid, go back to doing the work you're actually good at. Your admin time drops from 40% to 8%.",
  },
  {
    id: 'agency', label: 'Agencies', icon: Briefcase,
    pain: "Your 4-person team runs on 11 SaaS subscriptions. Projects slip because chat lives in Slack, files in Drive, invoices in QuickBooks. Client onboarding is a 2-week Slack dance. New hires take 6 weeks to become productive because context is everywhere and nowhere.",
    solution: "One app replaces the stack. Multi-brand portals mean you can serve 3 agencies from one backend. Onboarding wizards turn intake into a 10-minute form. Chief-of-staff AI tells your team what to tackle today. Net margin up 15%, typically.",
  },
  {
    id: 'dev-shop', label: 'Dev shops', icon: Code2,
    pain: "Your PM writes status updates on Monday. Clients ask for them on Wednesday. By Friday everyone's confused about scope. SOWs die in email threads. Bug reports are a blurry screenshot and 'it's broken'.",
    solution: "AI-drafted weekly reports auto-email clients. SOWs are e-signed in the portal. Bugs come with timestamped video annotations — 'see 0:42, here's the error'. Scope changes are formal Change Requests with cost + timeline delta, decided in-app.",
  },
  {
    id: 'consulting', label: 'Consulting', icon: Megaphone,
    pain: "You sell your brain by the hour. But 30% of your hours are invisible: explaining timelines, re-sending docs, reminding about invoices. Your $200/hr rate is effectively $140 after unbilled work.",
    solution: "Time tracker links straight to invoices — if you worked on it, it's billed. Auto-recurring retainer invoices. Client portal has live progress. The 'where are we at?' calls disappear because the answer is always visible.",
  },
  {
    id: 'automation', label: 'Automation / RPA', icon: Workflow,
    pain: "Your bots fail at 3am. Clients don't know until they see a data gap on Tuesday. Credential management is a shared 1Password vault that everyone forgets to rotate. Bug reports come as text messages.",
    solution: "Webhooks fire on SLA breach → Discord pings you mid-breakfast. Credentials live in an encrypted vault, rotatable per brand. Clients submit bugs with video annotations — no more guessing which screen broke.",
  },
  {
    id: 'saas', label: 'SaaS teams', icon: Rocket,
    pain: "Your customer success team lives in Intercom + Notion + Linear + your billing portal + your analytics tool. A single customer question takes 4 tab switches. Churn post-mortems are an archaeology dig.",
    solution: "Every customer is a brand inside Krris OS. One place for their invoices, SOW addenda, feature requests (Change Requests), NPS scores, and meeting notes. Audit log answers 'when did we promise that?' in one search.",
  },
  {
    id: 'legal', label: 'Legal / advisory', icon: Scale,
    pain: "You bill 6-minute increments on paper. You email contracts. Clients print, sign, scan, email back. You lose 2 hours/day to document logistics. Compliance audits are a three-day scavenger hunt.",
    solution: "E-signed SOWs and change orders with full audit trail. Every credential reveal, every document access — logged. Clients see exactly what they owe and pay in-app. The audit log IS the compliance report.",
  },
  {
    id: 'health', label: 'Health / wellness ops', icon: Stethoscope,
    pain: "Intake forms live on one tool, payment on another, client comms in email, session notes in a Google doc. HIPAA-style compliance is on your conscience, not on a platform.",
    solution: "Self-hosted deployment — your data stays in your infra, your control. Vault-encrypted intake, audit log on every access, role-based portal. Clients chat + pay + share files inside YOUR walls.",
  },
  {
    id: 'edu', label: 'Education / coaching', icon: GraduationCap,
    pain: "You teach 1-on-1. Your students ghost between sessions. You have no systematic way to give feedback on their homework videos. Billing is manual and you keep forgetting to send invoices.",
    solution: "Recurring invoices run weekly. Students upload homework videos; you annotate timestamps with feedback. NPS surveys trigger after each cohort. One portal per student, branded for your practice.",
  },
  {
    id: 'ecom', label: 'E-commerce ops', icon: ShoppingBag,
    pain: "You run 3 Shopify stores. Fulfillment tickets live in one tool, supplier payments in another, marketing assets on Google Drive. Onboarding a new VA takes a full week of Zoom handoffs.",
    solution: "Multi-brand support means one Krris OS instance serves all 3 stores. VAs get per-brand portal access. Task catalog standardizes SOPs. Knowledge base holds your supplier contacts + standard email templates.",
  },
];

// ---- Core principles ----
const PRINCIPLES = [
  { icon: Server, title: 'Self-hosted, by default',
    desc: 'Run Krris OS on your own box. One install.sh and you own the data. No vendor lock-in, no per-seat tax, no one reading your client contracts over your shoulder.' },
  { icon: Shield, title: 'Data stays in your intranet',
    desc: 'Your MongoDB, your R2, your Zoom account. The only outbound calls are the ones YOU configured (Claude, Stripe, webhooks). Nothing phones home. Ever.' },
  { icon: Lock, title: 'Encryption at rest, per brand',
    desc: 'Every vault entry encrypted with a brand-specific AES key. Reveal is audit-logged. Credentials never leak across tenants.' },
  { icon: Bot, title: 'AI you can turn off',
    desc: 'Claude integration is opt-in. Features gracefully degrade without it. Your core workflow works even when OpenAI is on fire.' },
  { icon: Users, title: 'Built for operators, not enterprises',
    desc: "No 'Schedule a demo' dark pattern. No usage quotas. No 'upgrade to Pro' toasts. One binary, all features, your keys." },
  { icon: ShieldCheck, title: 'Open by design',
    desc: 'Source code yours to fork. Replace any integration. Build any custom flow. The only wrong way to use this is the one that feels wrong to you.' },
];

// ---- Journey steps ----
const JOURNEY = [
  { icon: UserPlus, title: 'Signs up or submits wizard', body: 'Prospect fills in your branded intake — signup form or multi-step wizard. They choose their own password. Lead lands in your approval queue.' },
  { icon: FileCheck, title: 'You approve with one click', body: 'Review the requested project + AI lead score. Approve → client portal activates instantly. No temp password email needed — they log in with what they chose.' },
  { icon: FolderKanban, title: 'Project comes alive', body: 'Overview, SOW, chat, documents, invoices, time tracking, vault, calls, change requests — all pre-wired to the new project.' },
  { icon: Handshake, title: 'You collaborate in-app', body: 'Live chat, video annotations, file uploads, recurring invoices. Client never emails "did you see my message?" again.' },
  { icon: Rocket, title: 'Deliver and get paid', body: 'Stripe/PayPal/Razorpay built in. NPS survey fires on delivery. Referral ledger tracks introductions. You move to the next project with a thank-you note.' },
];

function FeatureCard({ f, flipped, onFlip, primary }) {
  const Icon = f.icon;
  return (
    <button
      onClick={onFlip}
      className="relative w-full h-44 rounded-card text-left"
      style={{ perspective: '1000px' }}
      data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 p-5 rounded-card bg-white border flex flex-col"
          style={{ borderColor: '#E8E3D9', backfaceVisibility: 'hidden' }}
        >
          <div className="w-10 h-10 rounded-btn flex items-center justify-center mb-3" style={{ background: `${primary}15` }}>
            <Icon size={18} style={{ color: primary }} />
          </div>
          <h3 className="font-black text-base mb-1" style={{ color: '#1A2428' }}>{f.title}</h3>
          <p className="text-xs mt-auto" style={{ color: '#5C6B72' }}>{f.desc}</p>
        </div>
        {/* Back */}
        <div
          className="absolute inset-0 p-5 rounded-card flex items-center"
          style={{
            background: primary, color: 'white',
            backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
          }}
        >
          <p className="text-sm font-semibold italic leading-snug">"{f.funny}"</p>
        </div>
      </motion.div>
    </button>
  );
}

export default function Features() {
  const branding = useBranding();
  const primary = branding.primary_color || '#006080';
  const [flipped, setFlipped] = useState({});
  const [activeDomain, setActiveDomain] = useState(DOMAINS[0].id);
  const [currentJourney, setCurrentJourney] = useState(0);
  const [typedPhraseIdx, setTypedPhraseIdx] = useState(0);

  const phrases = [
    'Close faster.',
    'Ghost less, deliver more.',
    'One portal. Many brands.',
    'AI does the boring bits.',
    'Your data, your box, your rules.',
  ];

  useEffect(() => {
    const t = setInterval(() => setTypedPhraseIdx(i => (i + 1) % phrases.length), 2800);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  // Auto-advance journey every 3.5s
  useEffect(() => {
    const t = setInterval(() => setCurrentJourney(i => (i + 1) % JOURNEY.length), 3500);
    return () => clearInterval(t);
  }, []);

  const activeDomainObj = DOMAINS.find(d => d.id === activeDomain);
  const ActiveDomainIcon = activeDomainObj.icon;

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif", color: '#1A2428' }}>
      {/* Animated bg */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(${primary} 1px, transparent 1px)`,
        backgroundSize: '32px 32px',
      }} />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
        className="fixed -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.08] pointer-events-none blur-3xl"
        style={{ background: `conic-gradient(from 0deg, ${primary}, #D9A441, ${primary})` }}
      />

      {/* Nav */}
      <nav className="relative z-10 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-btn flex items-center justify-center" style={{ background: primary }}>
            <Zap size={18} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-lg">{branding.app_name}</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <a href="#features" className="hidden md:inline font-semibold opacity-80 hover:opacity-100">Features</a>
          <a href="#domains" className="hidden md:inline font-semibold opacity-80 hover:opacity-100">Pain points</a>
          <a href="#journey" className="hidden md:inline font-semibold opacity-80 hover:opacity-100">Journey</a>
          <a href="#contact" className="hidden md:inline font-semibold opacity-80 hover:opacity-100">Contact</a>
          <Link to="/login" className="text-xs font-semibold px-3 py-1.5 rounded-btn" style={{ background: 'white', border: '1px solid #E8E3D9' }}>Sign in</Link>
          <a href="mailto:hello@krri.sh" className="text-xs font-bold px-3 py-1.5 rounded-btn text-white" style={{ background: primary }} data-testid="nav-contact">Get in touch</a>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-24 text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs uppercase tracking-[0.4em] font-bold mb-6"
          style={{ color: primary }}
        >
          {branding.app_name} · Agency on autopilot
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6"
        >
          Every client. Every project.<br />
          <span style={{ color: primary }}>One beautiful portal.</span>
        </motion.h1>
        <div className="h-12 mb-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={typedPhraseIdx}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-2xl sm:text-3xl font-bold"
              style={{ color: primary }}
            >
              {phrases[typedPhraseIdx]}
            </motion.p>
          </AnimatePresence>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-base sm:text-lg max-w-2xl mx-auto mb-10"
          style={{ color: '#5C6B72', lineHeight: 1.6 }}
        >
          The agency operating system for freelancers, consultancies, and dev shops.
          Self-hosted. AI-native. Built so you can stop juggling 11 tools and start shipping work.
        </motion.p>
        <div className="flex flex-wrap gap-3 justify-center">
          <a href="#features" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-white text-sm shadow-lg" style={{ background: primary }} data-testid="hero-see-features">
            See what's inside <ArrowDown size={14} />
          </a>
          <a href="mailto:hello@krri.sh" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-sm" style={{ background: 'white', border: '1px solid #E8E3D9', color: '#1A2428' }}>
            <Mail size={14} /> hello@krri.sh
          </a>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3 text-center" style={{ color: primary }}>Everything inside</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3 text-center">20 things we do for you.</h2>
        <p className="text-base mb-12 text-center max-w-2xl mx-auto" style={{ color: '#5C6B72' }}>
          Click any card. (We hid a joke on the back of each one because features sheets are boring.)
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {FEATURES.map(f => (
            <FeatureCard key={f.title} f={f} primary={primary} flipped={!!flipped[f.title]} onFlip={() => setFlipped(s => ({ ...s, [f.title]: !s[f.title] }))} />
          ))}
        </div>
      </section>

      {/* PAIN POINTS */}
      <section id="domains" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: primary }}>Who it's for</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3">Find your pain. We got you.</h2>
        <p className="text-base mb-12 max-w-2xl" style={{ color: '#5C6B72' }}>
          Pick the closest domain to yours. See what breaks, and what Krris OS does about it.
        </p>

        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible" data-testid="domain-tabs">
            {DOMAINS.map(d => {
              const Icon = d.icon;
              return (
                <button
                  key={d.id}
                  onClick={() => setActiveDomain(d.id)}
                  className="text-xs font-bold flex items-center gap-2 px-3 py-2.5 rounded-btn whitespace-nowrap transition-colors flex-shrink-0"
                  style={{
                    background: activeDomain === d.id ? primary : 'white',
                    color: activeDomain === d.id ? 'white' : '#1A2428',
                    border: `1px solid ${activeDomain === d.id ? primary : '#E8E3D9'}`,
                  }}
                  data-testid={`domain-${d.id}`}
                >
                  <Icon size={13} /> {d.label}
                </button>
              );
            })}
          </div>
          <motion.div
            key={activeDomain}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className="p-6 rounded-card bg-white border"
            style={{ borderColor: '#E8E3D9' }}
            data-testid="domain-content"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: `${primary}15` }}>
                <ActiveDomainIcon size={18} style={{ color: primary }} />
              </div>
              <h3 className="font-black text-xl">{activeDomainObj.label}</h3>
            </div>
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: '#B34A3A' }}>The pain</p>
              <p className="text-sm leading-relaxed" style={{ color: '#1A2428' }}>{activeDomainObj.pain}</p>
            </div>
            <div className="p-4 rounded-btn" style={{ background: `${primary}10`, borderLeft: `3px solid ${primary}` }}>
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: primary }}>How {branding.app_name} helps</p>
              <p className="text-sm leading-relaxed" style={{ color: '#1A2428' }}>{activeDomainObj.solution}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section id="principles" className="relative z-10 py-24" style={{ background: '#3D1B4E', color: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#F5C651' }}>Core principles</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-3" style={{ color: '#FFFFFF' }}>Ethics on the tin.</h2>
          <p className="text-base mb-12 max-w-2xl" style={{ color: '#E2D4EC' }}>
            How we build. What we promise. Non-negotiable.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRINCIPLES.map(p => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="p-5 rounded-card"
                  style={{ background: '#2A0F3D', border: '1px solid #5A2C6E' }}
                >
                  <div className="w-10 h-10 rounded-btn flex items-center justify-center mb-3" style={{ background: '#F5C65120' }}>
                    <Icon size={18} style={{ color: '#F5C651' }} />
                  </div>
                  <h3 className="font-black text-base mb-2" style={{ color: '#FFFFFF' }}>{p.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#E2D4EC' }}>{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section id="journey" className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3 text-center" style={{ color: primary }}>The whole journey</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3 text-center">From stranger to superfan.</h2>
        <p className="text-base mb-16 text-center max-w-2xl mx-auto" style={{ color: '#5C6B72' }}>
          Hover or click any step. The flow pauses where you are.
        </p>

        {/* Horizontal stepper */}
        <div className="grid md:grid-cols-5 gap-3 mb-8">
          {JOURNEY.map((j, i) => {
            const JIcon = j.icon;
            const isActive = currentJourney === i;
            return (
              <motion.button
                key={j.title}
                onClick={() => setCurrentJourney(i)}
                onMouseEnter={() => setCurrentJourney(i)}
                className="p-4 rounded-card text-left transition-colors"
                style={{
                  background: isActive ? primary : 'white',
                  color: isActive ? 'white' : '#1A2428',
                  border: `1px solid ${isActive ? primary : '#E8E3D9'}`,
                  boxShadow: isActive ? `0 8px 24px ${primary}40` : 'none',
                }}
                data-testid={`journey-step-${i}`}
                animate={{ scale: isActive ? 1.04 : 1 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-btn flex items-center justify-center" style={{ background: isActive ? 'rgba(255,255,255,0.2)' : `${primary}15` }}>
                    <JIcon size={14} style={{ color: isActive ? 'white' : primary }} />
                  </div>
                  <span className="font-mono text-xs font-bold" style={{ opacity: 0.6 }}>0{i + 1}</span>
                </div>
                <p className="font-black text-xs leading-tight">{j.title}</p>
              </motion.button>
            );
          })}
        </div>

        {/* Active step detail */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentJourney}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="p-8 rounded-card bg-white border"
            style={{ borderColor: '#E8E3D9' }}
            data-testid="journey-detail"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: primary }}>
                {React.createElement(JOURNEY[currentJourney].icon, { size: 24, color: 'white' })}
              </div>
              <div className="flex-1">
                <p className="text-xs font-mono font-bold uppercase mb-1" style={{ color: primary }}>Step 0{currentJourney + 1} of 0{JOURNEY.length}</p>
                <h3 className="font-black text-2xl mb-2">{JOURNEY[currentJourney].title}</h3>
                <p className="text-base leading-relaxed" style={{ color: '#5C6B72' }}>{JOURNEY[currentJourney].body}</p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-1" style={{ color: '#8A99A0' }}>
              {JOURNEY.map((_, i) => (
                <motion.div
                  key={i}
                  className="h-1 rounded-full flex-1"
                  animate={{ background: i <= currentJourney ? primary : '#E8E3D9' }}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* CTA */}
      <section id="contact" className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 size={32} style={{ color: primary, margin: '0 auto 20px' }} />
        <h2 className="text-4xl sm:text-5xl font-black mb-4">Ready to replace your stack?</h2>
        <p className="text-base max-w-xl mx-auto mb-3" style={{ color: '#5C6B72' }}>
          Email <a href="mailto:hello@krri.sh" className="font-bold underline" style={{ color: primary }}>hello@krri.sh</a> — we reply within 7 days with a tailored walkthrough + self-hosting instructions.
        </p>
        <p className="text-sm mb-10" style={{ color: '#8A99A0' }}>
          Or: clone the repo and self-host in 10 minutes. <Link to="/login" className="underline font-semibold" style={{ color: primary }}>See the docs →</Link>
        </p>
        <a href="mailto:hello@krri.sh?subject=Krris%20OS%20-%20Interested" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-white text-sm shadow-lg" style={{ background: primary }} data-testid="cta-email">
          <Mail size={14} /> hello@krri.sh
          <ArrowRight size={14} />
        </a>
      </section>

      <footer className="relative z-10 border-t py-8 text-center text-xs" style={{ borderColor: '#E8E3D9', color: '#8A99A0' }}>
        © {new Date().getFullYear()} {branding.app_name} by <Link to="/" className="font-semibold" style={{ color: primary }}>krri.sh</Link> · Self-hostable · MIT-style
      </footer>
    </div>
  );
}
